import axios from 'axios';
import apiClient from './client';
import UI_MESSAGES from 'i18n/messages';

let bearerToken = null;
let isConfigured = false;

const dispatchHttpErrorEvent = (payload) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('app:http-error', { detail: payload }));
};

const resolvePathFromError = (error) => {
  const requestUrl = error?.config?.url || '';
  if (!requestUrl) return '';

  try {
    if (requestUrl.startsWith('http://') || requestUrl.startsWith('https://')) {
      return new URL(requestUrl).pathname;
    }
  } catch (urlError) {
    // Ignore parsing error and fallback below.
  }

  return requestUrl;
};

const resolveDomainFromPath = (path) => {
  const normalizedPath = (path || '').toLowerCase();
  if (!normalizedPath) return 'unknown';
  if (normalizedPath.includes('/filereport/')) return 'fileReport';
  if (normalizedPath.includes('/reporting/')) return 'reporting';
  if (normalizedPath.includes('/monitor/')) return 'monitor';
  if (normalizedPath.includes('/device/')) return 'device';
  if (normalizedPath.includes('/user/')) return 'user';
  if (normalizedPath.includes('/entity/')) return 'entity';
  if (normalizedPath.includes('/locality/') || normalizedPath.includes('/geolocation/')) return 'locality';
  if (normalizedPath.includes('/event/')) return 'event';
  if (normalizedPath.includes('/realtime')) return 'realTime';
  return 'unknown';
};

const normalizeHttpError = (error) => {
  const status = error?.response?.status || 0;
  const payload = error?.response?.data;
  const path = resolvePathFromError(error);
  const method = (error?.config?.method || 'GET').toUpperCase();
  const domain = resolveDomainFromPath(path);
  const isNetworkError = !error?.response;
  const message = payload?.message || payload?.error || error?.message || UI_MESSAGES.logs.unexpectedNetworkError;

  return {
    status,
    message,
    payload,
    path,
    method,
    domain,
    isNetworkError,
    isUnauthorized: status === 401,
    isForbidden: status === 403,
    isServerError: status >= 500
  };
};

const shouldAttachBearerToken = (config, baseURL) => {
  const rawUrl = config?.url || '';
  if (!rawUrl) return true;

  try {
    const apiOrigin = new URL(baseURL).origin;
    const resolvedUrl = new URL(rawUrl, baseURL);
    return resolvedUrl.origin === apiOrigin;
  } catch (error) {
    // If URL parsing fails, keep conservative default for relative routes.
    return !String(rawUrl).startsWith('http://') && !String(rawUrl).startsWith('https://');
  }
};

export const configureHttpClient = () => {
  if (isConfigured) return;

  const baseURL = process.env.REACT_APP_API_BASE_URL || 'https://diiotcore.com';
  axios.defaults.baseURL = baseURL;
  axios.defaults.timeout = 30000;

  const requestInterceptor = (config) => {
    const nextConfig = { ...config };
    const legacyApiHost = 'https://diiotcore.com';

    // Remove legacy Basic Auth usage from older calls.
    if (Object.prototype.hasOwnProperty.call(nextConfig, 'auth')) {
      delete nextConfig.auth;
    }

    // Normalize legacy absolute URLs so every request can switch host by env.
    if (typeof nextConfig.url === 'string' && nextConfig.url.startsWith(legacyApiHost)) {
      nextConfig.url = nextConfig.url.replace(legacyApiHost, '');
    }

    if (!nextConfig.baseURL) {
      nextConfig.baseURL = baseURL;
    }

    if (bearerToken && shouldAttachBearerToken(nextConfig, baseURL)) {
      nextConfig.headers = {
        ...(nextConfig.headers || {}),
        Authorization: `Bearer ${bearerToken}`
      };
    } else if (nextConfig.headers?.Authorization) {
      // Prevent token leakage to external absolute URLs (e.g., signed URLs).
      const headersWithoutAuth = { ...(nextConfig.headers || {}) };
      delete headersWithoutAuth.Authorization;
      nextConfig.headers = headersWithoutAuth;
    }

    return nextConfig;
  };

  const responseErrorInterceptor = (error) => {
    const normalized = normalizeHttpError(error);
    error.appError = normalized;

    const shouldSkipGlobalErrorEvent = Boolean(error?.config?.skipGlobalErrorEvent);
    if (!shouldSkipGlobalErrorEvent) {
      dispatchHttpErrorEvent(normalized);
    }

    return Promise.reject(error);
  };

  axios.interceptors.request.use(requestInterceptor);
  apiClient.interceptors.request.use(requestInterceptor);
  axios.interceptors.response.use((response) => response, responseErrorInterceptor);
  apiClient.interceptors.response.use((response) => response, responseErrorInterceptor);

  isConfigured = true;
};

export const setHttpBearerToken = (token) => {
  bearerToken = token || null;
};
